import { makeStyles } from "@material-ui/core";
import createStyles from "@material-ui/core/styles/createStyles";
import React, { useEffect, useState } from "react";
import { FieldRenderProps } from "react-final-form";
import { ForeignKeyFieldOptions, LoggerFactory } from "../../../index";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import isNil from "lodash/isNil";
// @ts-ignore
import SanitizedHTML from "react-sanitized-html";
import { removeTags } from "../../../helpers/htmlUtils";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(() =>
  createStyles({
    inputBase: {
      marginTop: 4,
      padding: "3.5px !important",
    },
    paper: {
      width: "max-content",
      minWidth: "100%",
    },
    endAdornment: {
      position: "relative",
      right: "0px !important",
    },
  })
);

export interface SelectFieldWithAutocompleteProps
  extends ForeignKeyFieldOptions,
    FieldRenderProps<any> {
  options: any[];
  readOnly?: boolean;
}

interface SelectOption {}
type SelectValue = SelectOption | null;

export const SelectFieldWithAutocomplete = (
  props: SelectFieldWithAutocompleteProps
) => {
  const {
    meta,
    options,
    clearable,
    labelField,
    searchField,
    valueField,
    placeholder,
    renderSelectedOptions,
    dataSource,
    readOnly,
    input: { value, onFocus, onBlur },
    minWidth,
  } = props;

  const classes = useStyles();
  const i18n = useTranslation();
  const [val, setVal] = useState(null as SelectValue);
  const log = LoggerFactory.create(
    `SelectWithAutocomplete [${dataSource.name}]`
  );

  if (labelField instanceof Function && !searchField) {
    throw new Error("searchField is mandatory if labelField is a function");
  }

  const defaultRenderSelectedOptions = (option: SelectOption): string => {
    return removeTags(getOptionLabel(option));
  };

  useEffect((): void => {
    if ((isNil(value) || value === "") && !isNil(val)) {
      setVal(null);
      log.debug("value is null or empty so I am setting val to null");
      return;
    }
    let found = false;
    for (const element of dataSource.list) {
      // using Abstract Equality Comparison on purpose because value may be passed
      // through url and so be in a wrong format
      if (element[valueField] == value) {
        // we need all data if using Function to render custom label
        setVal(element);
        found = true;
        break;
      }
    }

    if (!found && dataSource.list.length > 0 && value) {
      log.debug("warning: did not find matching option", value);

      // it fixes internal state to represent currently selected option (none)
      setVal(null);
    }
  }, [dataSource.list, log, val, value, valueField]);

  const handleChange = (event: object, value: any): void => {
    log.debug("select changed", value, event);

    value !== null
      ? props.input.onChange(value[valueField])
      : props.input.onChange(undefined);
  };

  const getOptionLabel = (option: SelectOption): string => {
    return labelField instanceof Function
      ? labelField(option)
      : option[labelField];
  };

  // @ts-ignore
  const renderOptions = (option: SelectOption): string | JSX => {
    return <SanitizedHTML html={getOptionLabel(option)} />;
  };

  return (
    <Autocomplete
      style={{ minWidth: minWidth || 150 }}
      classes={{
        inputRoot: classes.inputBase,
        paper: classes.paper,
        endAdornment: classes.endAdornment,
      }}
      options={options}
      disabled={readOnly}
      disableClearable={!clearable}
      getOptionLabel={
        renderSelectedOptions
          ? renderSelectedOptions
          : defaultRenderSelectedOptions
      }
      renderOption={renderOptions}
      getOptionSelected={(
        option: SelectOption,
        value: SelectValue
      ): boolean => {
        return !isNil(value) && value[valueField] == option[valueField];
      }}
      value={val}
      onOpen={(event) => {
        log.debug("open");
        // @ts-ignore
        onFocus(event);
      }}
      onClose={(event) => {
        log.debug("close");
        // @ts-ignore
        onBlur(event);
      }}
      onChange={handleChange}
      size="small"
      renderInput={(params) => (
        <TextField
          {...params}
          helperText={
            meta.visited || meta.touched
              ? meta.error || meta.submitError
              : undefined
          }
          error={(meta.error || meta.submitError) && meta.touched}
          variant="outlined"
          placeholder={placeholder || i18n.t("lego:select", "Select")}
          fullWidth
        />
      )}
    />
  );
};
